import { DOC_SEARCH_DWELL } from 'features/analytics'
import { ChatbotSidePanel, useChatbotAttachments } from 'features/chatbot'
import { ArtifactPanel } from 'features/knowledgeDocumentViewer/artifacts/ArtifactPanel'
import { useStoredArtifact } from 'features/knowledgeDocumentViewer/artifacts/artifactStore'
import { findLatestArtifactEventInTabs } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import { ContextMenu } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { DocumentTabDataProvider } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { SearchActionsProvider } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { SearchResultsNavigationProvider } from 'features/knowledgeDocumentViewer/contexts/SearchResultsNavigationContext'
import { GraphNodeSelectionProvider } from 'features/knowledgeDocumentViewer/graph/GraphNodeSelectionContext'
import { useFavoriteDocuments } from 'features/knowledgeDocumentViewer/hooks/useFavoriteDocuments'
import type { IntraDocSearchUiState } from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import { LinkedDocumentsNavigationResult } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  type SendPowerAnalyticsEvent,
  usePowerAnalyticsBackend,
} from 'features/knowledgeDocumentViewer/hooks/usePowerAnalyticsBackend'
import { useRecentItems } from 'features/knowledgeDocumentViewer/hooks/useRecentItems'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import {
  ArtifactHistoryItem,
  DocumentHistoryItem,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import {
  getPersistedChatbotState,
  persistChatbotState,
} from 'features/knowledgeDocumentViewer/utils/documentPageSessionStorage'
import {
  getKeepAliveSearchHistoryEntry,
  isSearchTabVisible,
  nextMountedSearchTabIds,
} from 'features/knowledgeDocumentViewer/utils/keepAliveSearchTab'
import { linkedDocsNavigationToRecentAsset } from 'features/knowledgeDocumentViewer/utils/recentItemsStorage'
import { useWorkOrder } from 'features/knowledgeDocumentViewer/workOrders/useWorkOrder'
import {
  cmmsRecordNumber,
  isCmmsRecordId,
} from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { WorkOrderViewer } from 'features/knowledgeDocumentViewer/workOrders/WorkOrderViewer'
import { useOptionalWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useWorkspaceTabTitle } from 'features/workspaceTabs/hooks/useWorkspaceTabTitle'
import { resolvePublishedSearchTabTitle } from 'features/workspaceTabs/utils/getWorkspaceTabLabel'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DocumentTableRow } from './advancedSearch/components/documentsTableView/DocumentTableColumns'
import { AdvancedSearchPage } from './advancedSearch/components/search/AdvancedSearchPage'
import { BoxContextMenuState, DocumentTabContent } from './DocumentTabContent'
import {
  DocumentContentWrapper,
  EditorContainer,
  EditorMainColumn,
  KeepAlivePanel,
  MainContentWrapper,
  TabContentPanel,
  TabTransitionOverlay,
  ViewerContainer,
} from './knowledgeDocumentToolsStyles'
import { DocumentPreviewModeToolbar } from './toolbar/DocumentPreviewModeToolbar'
import { DocumentTabsBar } from './toolbar/DocumentTabsBar'
import type { DocumentViewerViewportState } from './viewer/shared/documentViewerViewport'

type StoredDocumentViewport = {
  documentId: string
  viewport: DocumentViewerViewportState
}

function getCurrentDocumentHistoryItem(
  tab: DocumentTab
): DocumentHistoryItem | undefined {
  const item = tab.history[tab.currentIndex]
  return item?.type === 'document' ? item : undefined
}

function getCurrentArtifactHistoryItem(
  tab: DocumentTab
): ArtifactHistoryItem | undefined {
  const item = tab.history[tab.currentIndex]
  return item?.type === 'artifact' ? item : undefined
}

export function KnowledgeDocumentTools({
  scale = 1.5,
}: {
  scale?: number
  fitOnLoad?: boolean
}) {
  // Provider presence gates legacy DocumentTabs; delete with workspace rollout.
  const workspaceTabs = useOptionalWorkspaceTabs()
  const {
    siteId: siteID,
    activeTabId,
    openTabs,
    currentDocumentId,
    currentSearchOriginQuery,
    updateDocumentHistoryItem,
    isGeckoUser,
    viewLayout,
    isEvalSessionLocked,
  } = useKnowledgeDocumentUXContext()

  const { recentItems, addRecentItem } = useRecentItems(siteID)
  const {
    isFavorited,
    toggleFavorite,
    isReady: favoritesReady,
    isSaving: favoritesSaving,
  } = useFavoriteDocuments(siteID)
  const { sendPowerAnalyticsEvent } = usePowerAnalyticsBackend(siteID)
  const { document: attachmentDoc } = useSystemDocumentByBqId(
    currentDocumentId || undefined
  )

  const [isChatbotOpen, setIsChatbotOpen] = useState(
    () => getPersistedChatbotState(siteID)?.isChatbotOpen ?? false
  )

  const dwellRef = useRef<{
    documentId: string
    startedAt: number
    query: string
  } | null>(null)
  const dwellAnalyticsRef = useRef<{
    sendEvent: SendPowerAnalyticsEvent
    siteId: string
  }>({
    sendEvent: sendPowerAnalyticsEvent,
    siteId: siteID,
  })
  dwellAnalyticsRef.current = {
    sendEvent: sendPowerAnalyticsEvent,
    siteId: siteID,
  }

  const currentDocRef = useRef({
    id: currentDocumentId,
    query: currentSearchOriginQuery,
  })
  currentDocRef.current = {
    id: currentDocumentId,
    query: currentSearchOriginQuery,
  }

  const fireDwell = useCallback((options?: { keepalive?: boolean }) => {
    if (!dwellRef.current) return
    const duration = Math.round(performance.now() - dwellRef.current.startedAt)
    const { sendEvent, siteId: site } = dwellAnalyticsRef.current
    if (duration >= 1000) {
      const dwellPayload = {
        document_id: dwellRef.current.documentId,
        dwell_duration_ms: duration,
        query: dwellRef.current.query,
        site_id: site,
      }
      sendEvent(DOC_SEARCH_DWELL.title, dwellPayload, options)
    }
    dwellRef.current = null
  }, [])

  const fireDwellBeacon = useCallback(
    () => fireDwell({ keepalive: true }),
    [fireDwell]
  )

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      fireDwellBeacon()
    } else if (document.visibilityState === 'visible') {
      const { id, query } = currentDocRef.current
      if (id && !dwellRef.current) {
        dwellRef.current = {
          documentId: id,
          startedAt: performance.now(),
          query,
        }
      }
    }
  }, [fireDwellBeacon])

  useEffect(() => {
    fireDwell()

    if (currentDocumentId) {
      dwellRef.current = {
        documentId: currentDocumentId,
        startedAt: performance.now(),
        query: currentDocRef.current.query,
      }
    }

    return () => fireDwell()
  }, [currentDocumentId, fireDwell])

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', fireDwellBeacon)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', fireDwellBeacon)
    }
  }, [handleVisibilityChange, fireDwellBeacon])

  const { addAttachment } = useChatbotAttachments()

  const handleLinkedDocsNavigationSuccess = useCallback(
    (result: LinkedDocumentsNavigationResult) => {
      const asset = linkedDocsNavigationToRecentAsset(result)
      if (asset) addRecentItem(asset)
    },
    [addRecentItem]
  )

  const [contextMenuState, setContextMenuState] =
    useState<BoxContextMenuState | null>(null)
  const [mountedSearchTabIds, setMountedSearchTabIds] = useState<Set<string>>(
    () => new Set()
  )
  const [mountedDocumentTabIds, setMountedDocumentTabIds] = useState<
    Set<string>
  >(() => new Set())

  const searchResultsByTabRef = useRef<Map<string, DocumentTableRow[]>>(
    new Map()
  )

  const intraSearchStateByTabRef = useRef<Map<string, IntraDocSearchUiState>>(
    new Map()
  )

  const viewerViewportByTabRef = useRef<Map<string, StoredDocumentViewport>>(
    new Map()
  )

  const handleBoxContextMenuChange = useCallback(
    (state: BoxContextMenuState | null) => {
      setContextMenuState(state)
    },
    []
  )

  const handleSnapshotSearchResults = useCallback(
    (tabId: string, results: DocumentTableRow[]) => {
      searchResultsByTabRef.current.set(tabId, [...results])
    },
    []
  )

  const getSearchResultsForTab = useCallback(
    (tabId: string) => searchResultsByTabRef.current.get(tabId),
    []
  )

  const getIntraSearchStateForTab = useCallback(
    (tabId: string) => intraSearchStateByTabRef.current.get(tabId),
    []
  )

  const handleIntraSearchStateChange = useCallback(
    (tabId: string, state: IntraDocSearchUiState) => {
      intraSearchStateByTabRef.current.set(tabId, state)
    },
    []
  )

  const getViewerViewportForTab = useCallback(
    (tabId: string, documentId: string) => {
      const stored = viewerViewportByTabRef.current.get(tabId)
      if (!stored || stored.documentId !== documentId) {
        return null
      }
      return stored.viewport
    },
    []
  )

  const handleViewerViewportChange = useCallback(
    (
      tabId: string,
      documentId: string,
      viewport: DocumentViewerViewportState
    ) => {
      const existing = viewerViewportByTabRef.current.get(tabId)
      // Ignore brief paged mounts while multipage metadata is still loading.
      if (
        viewLayout === 'continuous' &&
        existing &&
        existing.documentId === documentId &&
        existing.viewport.kind === 'continuous' &&
        viewport.kind === 'paged'
      ) {
        return
      }
      viewerViewportByTabRef.current.set(tabId, { documentId, viewport })
    },
    [viewLayout]
  )

  const handleNavigateToSearchResult = useCallback(
    (documentId: string) => {
      updateDocumentHistoryItem(activeTabId, {
        documentId,
        pageNumber: 1,
        selectedBoxIds: [],
      })
    },
    [activeTabId, updateDocumentHistoryItem]
  )

  const renderDocumentTab = useCallback(
    (tab: DocumentTab, historyItem: DocumentHistoryItem, isActive: boolean) => {
      const tabSearchResults = getSearchResultsForTab(tab.id)
      const showNav =
        isGeckoUser && tabSearchResults && tabSearchResults.length > 0

      const content = isCmmsRecordId(historyItem.documentId) ? (
        <WorkOrderViewer
          key={historyItem.documentId}
          documentId={historyItem.documentId}
          initialPage={historyItem.pageNumber}
          onPageChange={(pageNumber) =>
            updateDocumentHistoryItem(tab.id, { pageNumber })
          }
          citedQuotes={historyItem.fragmentHighlight?.quotes}
          expectedVersion={historyItem.fragmentHighlight?.sourceVersion}
          expectedDocumentRevision={
            historyItem.fragmentHighlight?.cmmsDocumentRevision
          }
          citedNodeIds={historyItem.fragmentHighlight?.cmmsNodeIds}
          fullTab
          isActive={isActive}
        />
      ) : (
        <DocumentTabDataProvider
          documentId={historyItem.documentId}
          pageNumber={historyItem.pageNumber}
          siteID={siteID}
          panelSubjectUid={historyItem.selectedSubjectUid}>
          <DocumentTabContent
            tabId={tab.id}
            documentId={historyItem.documentId}
            pageNumber={historyItem.pageNumber}
            selectedBoxIds={historyItem.selectedBoxIds}
            selectedSubjectUid={historyItem.selectedSubjectUid}
            lotoPanel={historyItem.lotoPanel}
            scale={scale}
            isActiveTab={isActive}
            setIsChatbotOpen={setIsChatbotOpen}
            onBoxContextMenuChange={handleBoxContextMenuChange}
            linkedDocsNavigationOptions={{
              onNavigationSuccess: handleLinkedDocsNavigationSuccess,
            }}
            fragmentHighlight={historyItem.fragmentHighlight}
            regionHighlight={historyItem.regionHighlight}
            initialIntraSearchState={getIntraSearchStateForTab(tab.id)}
            onIntraSearchStateChange={(state) =>
              handleIntraSearchStateChange(tab.id, state)
            }
            initialViewport={getViewerViewportForTab(
              tab.id,
              historyItem.documentId
            )}
            onViewportChange={(viewport) =>
              handleViewerViewportChange(
                tab.id,
                historyItem.documentId,
                viewport
              )
            }
          />
        </DocumentTabDataProvider>
      )

      return (
        <TabContentPanel>
          <GraphNodeSelectionProvider
            initialNodeId={historyItem.selectedGraphNodeId}>
            {showNav ? (
              <SearchResultsNavigationProvider
                resultDocuments={tabSearchResults}
                currentDocumentId={historyItem.documentId}
                onNavigateToDocument={handleNavigateToSearchResult}>
                {content}
              </SearchResultsNavigationProvider>
            ) : (
              content
            )}
          </GraphNodeSelectionProvider>
        </TabContentPanel>
      )
    },
    [
      isGeckoUser,
      updateDocumentHistoryItem,
      siteID,
      scale,
      getSearchResultsForTab,
      getIntraSearchStateForTab,
      handleIntraSearchStateChange,
      getViewerViewportForTab,
      handleViewerViewportChange,
      handleBoxContextMenuChange,
      handleLinkedDocsNavigationSuccess,
      handleNavigateToSearchResult,
      setIsChatbotOpen,
    ]
  )

  useEffect(() => {
    const openTabIds = new Set(openTabs.map((tab) => tab.id))
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    const activeSearchTabId =
      activeTab?.history[activeTab.currentIndex]?.type === 'search'
        ? activeTabId
        : undefined

    setMountedSearchTabIds((prev) => {
      const next = nextMountedSearchTabIds(prev, openTabIds, activeSearchTabId)
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev
      }
      return next
    })
  }, [openTabs, activeTabId])

  useEffect(() => {
    const openDocumentTabIds = new Set(
      openTabs
        .filter((tab) => tab.history[tab.currentIndex]?.type === 'document')
        .map((tab) => tab.id)
    )
    const activeDocumentTabId = openDocumentTabIds.has(activeTabId)
      ? activeTabId
      : undefined

    setMountedDocumentTabIds((prev) => {
      const next = new Set(
        [...prev].filter((tabId) => openDocumentTabIds.has(tabId))
      )
      if (activeDocumentTabId) {
        next.add(activeDocumentTabId)
      }
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev
      }
      return next
    })
  }, [openTabs, activeTabId])

  const renderKeepAliveDocumentTabs = useCallback(
    () =>
      openTabs.flatMap((tab) => {
        const item = getCurrentDocumentHistoryItem(tab)
        if (
          !item ||
          (!mountedDocumentTabIds.has(tab.id) && tab.id !== activeTabId)
        ) {
          return []
        }
        const isActive = tab.id === activeTabId
        return [
          <KeepAlivePanel key={`doc-${tab.id}`} $active={isActive}>
            {renderDocumentTab(tab, item, isActive)}
          </KeepAlivePanel>,
        ]
      }),
    [openTabs, mountedDocumentTabIds, activeTabId, renderDocumentTab]
  )

  const renderKeepAliveArtifactTabs = useCallback(
    () =>
      openTabs.flatMap((tab) => {
        const item = getCurrentArtifactHistoryItem(tab)
        if (!item) return []
        const isActive = tab.id === activeTabId
        return [
          <KeepAlivePanel key={`artifact-${tab.id}`} $active={isActive}>
            <TabContentPanel>
              <ArtifactPanel
                artifactId={item.artifactId}
                siteId={siteID}
                latestEvent={findLatestArtifactEventInTabs(
                  openTabs,
                  item.artifactId
                )}
              />
            </TabContentPanel>
          </KeepAlivePanel>,
        ]
      }),
    [openTabs, activeTabId, siteID]
  )

  const renderKeepAliveSearchTabs = useCallback(
    () =>
      openTabs.flatMap((tab) => {
        const item = getKeepAliveSearchHistoryEntry(tab)?.item
        const isVisible = isSearchTabVisible(tab, activeTabId)
        if (!item || (!mountedSearchTabIds.has(tab.id) && !isVisible)) {
          return []
        }
        const linkedDocuments = item.linkedDocuments
        return [
          <KeepAlivePanel key={tab.id} $active={isVisible}>
            <AdvancedSearchPage
              siteId={siteID}
              tabId={tab.id}
              isActive={isVisible}
              searchState={item.searchState}
              linkedDocIds={linkedDocuments?.documentIds}
              unitId={linkedDocuments?.unitId}
            />
          </KeepAlivePanel>,
        ]
      }),
    [openTabs, mountedSearchTabIds, activeTabId, siteID]
  )

  useEffect(() => {
    persistChatbotState(siteID, isChatbotOpen)
  }, [siteID, isChatbotOpen])

  const { entry: activeWorkOrder } = useWorkOrder(
    currentDocumentId ?? undefined
  )
  const documentTabTitle = useMemo(() => {
    if (isCmmsRecordId(currentDocumentId))
      return activeWorkOrder ? cmmsRecordNumber(activeWorkOrder) : undefined

    if (!currentDocumentId || !attachmentDoc) return undefined
    const filename = attachmentDoc.entry.data?.filename ?? currentDocumentId
    return (
      attachmentDoc.entry.data?.document_title ??
      attachmentDoc.entry.data?.title ??
      filename
    )
  }, [attachmentDoc, currentDocumentId, activeWorkOrder])

  // Republish the history-derived search label when leaving a document so
  // customLabel does not keep the document title after back navigation.
  const searchTabTitle = useMemo(() => {
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    return resolvePublishedSearchTabTitle({
      currentDocumentId,
      tab: activeTab,
    })
  }, [activeTabId, currentDocumentId, openTabs])

  const activeArtifactId = useMemo(() => {
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    return activeTab
      ? getCurrentArtifactHistoryItem(activeTab)?.artifactId
      : undefined
  }, [activeTabId, openTabs])
  const activeArtifactTitle = useStoredArtifact(activeArtifactId)?.title

  useWorkspaceTabTitle(
    documentTabTitle ?? activeArtifactTitle ?? searchTabTitle,
    { followActiveTab: true }
  )

  useEffect(() => {
    const selectedDocument = attachmentDoc
    if (!currentDocumentId || !selectedDocument) {
      return
    }

    const filename = selectedDocument.entry.data?.filename ?? currentDocumentId
    const displayTitle =
      selectedDocument.entry.data?.document_title ??
      selectedDocument.entry.data?.title ??
      filename

    addAttachment({
      id: currentDocumentId,
      type: 'system_document',
      name: filename,
      displayTitle,
    })
  }, [currentDocumentId, attachmentDoc, addAttachment])

  useEffect(() => {
    const currentTabIds = new Set(openTabs.map((t) => t.id))
    for (const tabId of searchResultsByTabRef.current.keys()) {
      if (!currentTabIds.has(tabId)) {
        searchResultsByTabRef.current.delete(tabId)
      }
    }
    for (const tabId of intraSearchStateByTabRef.current.keys()) {
      if (!currentTabIds.has(tabId)) {
        intraSearchStateByTabRef.current.delete(tabId)
      }
    }
    for (const tabId of viewerViewportByTabRef.current.keys()) {
      if (!currentTabIds.has(tabId)) {
        viewerViewportByTabRef.current.delete(tabId)
      }
    }
  }, [openTabs])

  return (
    <SearchActionsProvider
      recentItems={recentItems}
      addRecentItem={addRecentItem}
      onOpenChatbot={() => setIsChatbotOpen(true)}
      onSnapshotSearchResults={handleSnapshotSearchResults}
      isFavorited={isFavorited}
      favoritesDisabled={!favoritesReady}
      favoritesSaving={favoritesSaving}
      toggleFavorite={toggleFavorite}>
      <EditorContainer>
        <EditorMainColumn>
          {isEvalSessionLocked ? null : workspaceTabs ? (
            <DocumentPreviewModeToolbar />
          ) : (
            <DocumentTabsBar />
          )}
          <MainContentWrapper>
            <DocumentContentWrapper>
              <ViewerContainer>
                <TabTransitionOverlay key={`tab-transition-${activeTabId}`} />
                {renderKeepAliveDocumentTabs()}
                {renderKeepAliveArtifactTabs()}
                {renderKeepAliveSearchTabs()}
              </ViewerContainer>
            </DocumentContentWrapper>
          </MainContentWrapper>
        </EditorMainColumn>

        {isEvalSessionLocked ? null : (
          <ChatbotSidePanel
            isOpen={isChatbotOpen}
            onClose={() => setIsChatbotOpen(false)}
          />
        )}

        {isEvalSessionLocked ? null : (
          <ContextMenu
            onCrossReference={contextMenuState?.onCrossReference}
            position={contextMenuState?.position ?? null}
            onClose={contextMenuState?.onClose ?? (() => {})}
            onOpen={contextMenuState?.onOpen ?? (() => {})}
            onOpenInNewTab={contextMenuState?.onOpenInNewTab ?? (() => {})}
            onAddToContext={contextMenuState?.onAddToContext ?? (() => {})}
            showNavigationOptions={
              contextMenuState?.showNavigationOptions ?? false
            }
            showAddToContext={contextMenuState?.showAddToContext ?? false}
          />
        )}
      </EditorContainer>
    </SearchActionsProvider>
  )
}
